<script setup lang="ts">
import { storeToRefs } from "pinia";
import type { FunctionalComponent, SVGAttributes } from "vue";
import { useToggle } from "~/core/composables/useToggle";
import type LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationError,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useActivateUser } from "~/modules/user/composables/useActivateUser";
import { useDeactivateUser } from "~/modules/user/composables/useDeactivateUser";
import { useDeleteUser } from "~/modules/user/composables/useDeleteUser";
import type { User } from "~/modules/user/models/User.interface";
import { UserActions } from "~/modules/user/models/UserActions.enum";
import type { UserDetails } from "~/modules/user/models/UserDetails.interface";
import type { UserProfile } from "~/modules/user/models/UserProfile.interface";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { isProfileAuthorized } from "~/modules/user/utils/isProfileAuthorized";
import ILxcPauseCircle from "~icons/lxc-light/pause-circle";
import ILxcPlayCircle from "~icons/lxc-light/play-circle";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  user: User;
  action: UserActions;
  businessUnitCode?: number;
  organizationCode?: number;
  currentUser?: UserDetails;
  allProfiles?: UserProfile[];
}>();

const emit = defineEmits(["change"]);

const { t } = useI18n();
const {
  isLoading: isDeleting,
  error: deletionError,
  deleteUser,
} = useDeleteUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const {
  isLoading: isActivating,
  error: activationError,
  activateUser,
} = useActivateUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});
const {
  isLoading: isDeactivating,
  error: deactivationError,
  deactivateUser,
} = useDeactivateUser({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

const { userSession } = storeToRefs(useUserSession());

const [deleteModalVisible, toggleDeleteModalVisible] = useToggle();

interface ActionConfiguration {
  icon: FunctionalComponent<SVGAttributes>;
  title: string;
  triggeredAction: (() => void) | ((userLogin: string) => void);
  disabled?: boolean;
}

const canProcessAction = computed(() => {
  if (!props.allProfiles || !props.currentUser) {
    return false;
  }
  if (props.user.profile.id !== props.currentUser.profile.id) {
    return isProfileAuthorized(props.allProfiles, props.user.profile.id);
  }
  return true;
});
const isActionDisabled = computed(
  () => userSession.value?.login === props.user.login,
);

const actionConfiguration: ComputedRef<ActionConfiguration> = computed(() => {
  if (props.action === UserActions.DELETE) {
    return {
      icon: ILxcLightTrash2,
      title: canProcessAction.value
        ? t("user.button.delete")
        : t("user.button.disabled.unauthorized"),
      triggeredAction: onDeleteUserClick,
      disabled: isActionDisabled.value || !canProcessAction.value,
    };
  } else if (
    props.action === UserActions.ACTIVATE_DEACTIVATE &&
    props.user.active
  ) {
    return {
      icon: ILxcPauseCircle,
      title: canProcessAction.value
        ? t("user.button.deactivate")
        : t("user.button.disabled.unauthorized"),
      triggeredAction: onDeactivateUser,
      disabled: isActionDisabled.value || !canProcessAction.value,
    };
  } else {
    return {
      icon: ILxcPlayCircle,
      title: canProcessAction.value
        ? t("user.button.activate")
        : t("user.button.disabled.unauthorized"),
      triggeredAction: onActivateUser,
      disabled: isActionDisabled.value || !canProcessAction.value,
    };
  }
});

const isProcessingAction = computed(
  () => isActivating.value || isDeactivating.value || isDeleting.value,
);

const buttonClass = computed(() => {
  const buttonClass = ["!px-0", "!py-0"];

  if (!isProcessingAction.value) {
    buttonClass.push("invisible");
  }

  return buttonClass;
});

async function onDeleteUser(userLogin: string) {
  await deleteUser(userLogin);
  toggleDeleteModalVisible();

  if (deletionError.value) {
    showNotificationError(t(NotificationKey.removeError));
  } else {
    showNotificationSuccess(t(NotificationKey.removeSuccess));
    emit("change");
  }
}

const onDeleteUserClick = () => {
  toggleDeleteModalVisible(true);
};

const checkActivateDeactivateResponse = (error?: LxcError) => {
  if (error) {
    showNotificationError(t(NotificationKey.error));
  } else {
    showNotificationSuccess(t(NotificationKey.success));
    emit("change");
  }
};

const onActivateUser = async (userLogin: string) => {
  await activateUser(userLogin);
  checkActivateDeactivateResponse(activationError.value);
};

const onDeactivateUser = async (userLogin: string) => {
  await deactivateUser(userLogin);
  checkActivateDeactivateResponse(deactivationError.value);
};
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :icon="!isProcessingAction ? actionConfiguration.icon : undefined"
    :title="actionConfiguration.title"
    :is-loading="isProcessingAction"
    :class="buttonClass"
    :disabled="actionConfiguration.disabled"
    @click.stop.prevent="actionConfiguration.triggeredAction(user.login)"
  />
  <confirm-modal
    :is-dialog-visible="deleteModalVisible"
    :title="t('user.confirmRemove.title')"
    :description="
      t('user.confirmRemove.description', {
        userName: user?.lastName,
        userFirstname: user?.firstName,
      })
    "
    :icon="ILxcLightTrash2"
    :disabled-confirm="isDeleting"
    :is-loading="isDeleting"
    icon-color-theme="error"
    color-theme="danger"
    @cancel="toggleDeleteModalVisible"
    @confirm="onDeleteUser(user.login)"
  />
</template>

<style scoped lang="scss">
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
